/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { DataScope } from './surveyDataScope';
import { RequesterMessageFormFields } from '@features/message-modal/types';


export enum DataShareStatus {
  Pending = 'pending',
  Accepted = 'accepted',
  Expired = 'expired',
  Revoked = 'revoked',
  Deleted = 'deleted',
}

export enum RequesterType {
  Portfolio = 'portfolio',
}

export interface DataShareCreateBasic {

  /**
   * Custom title of request message to target organization
   * Should be limited to as short title, limited to 255 characters
   */
  title: string,

  /** Custom content that provided additional context */
  content?: string,

  /** Represent the source data initiative, aka Organization, RootInitiative */
  initiativeId: string;

  /** Represents requester entity that can now use shared data */
  requesterId: string;

  /**
   * What requesterId is referencing:
   * - Portfolio (PT)
   * - Company, Financial Organization (CTL or CT) etc.
   * - Share Token etc.
   */
  requesterType: RequesterType;
  /**
   * Constrains of the data that is shared through this request
   */
  dataScope?: DataScope;
}

export enum RestrictionType {
  Subscription = 'subscription',
  Sponsorship = 'sponsorship',
  Staff = 'staff',
  /**
   * We have restriction, but it's not enforced. Same as other types have expired.
   * endDate does do anything in this case. It direct opposite of Sponsorship type
   */
  NotRestricted = 'not_restricted',
}

interface RestrictionSubscriptionData {
  initiativeId: string;
  subscriptionId: string;
}

interface RestrictionSponsorshipData {
  /** Reference to sponsorship object that cause this restriction */
  sponsorshipId: string;
}

/** Details when staff applied enforced restriction */
interface RestrictionStaff {
  userId: string;
}

/**
 * Reason why this share request is restricted.
 *
 * @example Organization is sponsored through subscription, hence some data access
 * is mandatory and cannot be revoked or managed in any way until the restriction
 * is lifted
 */
interface BaseRestriction<T extends RestrictionType, D = unknown> {
  type: T;

  /**
   * Allow to set end date of the restriction
   */
  endDate?: Date;

  /** Additional message for reason/context of this constrain */
  message?: string;

  /** Generic data based on the type value that allow to manage restriction */
  data: D;
}

// Union of all possible restriction types, should be added here as option
export type Restriction = | BaseRestriction<RestrictionType.Subscription, RestrictionSubscriptionData>
| BaseRestriction<RestrictionType.Staff, RestrictionStaff>
| BaseRestriction<RestrictionType.Sponsorship, RestrictionSponsorshipData>
| BaseRestriction<RestrictionType.NotRestricted, RestrictionSponsorshipData>

export interface DataShare extends DataShareCreateBasic {

  /** model ObjectId in string format */
  _id: string;

  /**
   * Represent restriction on the share request where initiative cannot modify
   * it directly due to applied restriction.
   *
   * Example is sponsorship of the subscription, that allow the Sponsor to access
   * the data
   */
  restriction?: Restriction;

  /**
   * When share request was accepted
   */
  acceptedDate?: string;

  /**
   * Represent the expiry date of the share request
   * After this period, share request should no longer be valid
   *
   * If end date is not available, then share request never expire and will be
   * valid until manually revoked.
   */
  expiredDate?: string;

  /**
   * Revoked represents when this share request was revoked
   */
  revokedDated?: string;

  /**
   * DeletedDate represents when this share request was revoked aka deleted
   */
  deletedDate?: string;

  /**
   * When data share request was created
   */
  created: string;

  status: DataShareStatus;
}

export type DataShareMin = Pick<DataShare, '_id' | 'initiativeId' | 'status' | 'dataScope' | 'restriction'>;

export interface DataShareWithRequester extends DataShare {
  requester: {
    _id: string;
    name: string;
  }
  status: DataShareStatus;
}

export type RequesterLookup = Pick<DataShare, | 'requesterId' | 'requesterType' | 'initiativeId'>

export type RequesterDetails = Pick<DataShare,'requesterId' | 'requesterType' > & { name: string };

export interface RequesterMessageRequest {
  initiativeId: string;
  requesterType: string;
  requesterId: string;
  payload: RequesterMessageFormFields;
}
